'use client'

// Top-level error boundary. Catches anything that bubbles past every
// segment-level error.jsx — i.e. errors that happen in the root layout
// itself, or in segments that don't define their own boundary.
//
// PHILOSOPHY: the customer never sees an angry error card. We report
// every error to our ingest endpoint with full context so the root cause
// can be diagnosed and fixed at the source — NOT papered over with
// retry/reload loops. If an error does reach this boundary, we show a
// neutral loading spinner (matches the rest of the app's loading state).
// A "Try again" button is offered so the user can manually retry only
// when THEY decide to — no surprise reloads in the middle of their work.

import { useEffect, useRef } from 'react'
import { reportClientError } from '../lib/clientErrorReporter'

export default function GlobalError({ error, reset }) {
  const reportedRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (reportedRef.current) return
    reportedRef.current = true

    // eslint-disable-next-line no-console
    console.error('[global-error]', error)
    reportClientError({
      message: error?.message || 'global-error boundary fired',
      stack: error?.stack,
      digest: error?.digest,
      category: 'boundary',
      context: { boundary: 'global-error' },
    })
  }, [error])

  return (
    <html lang="en">
      <body style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: '#FAF7F2', color: '#0F172A', margin: 0, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', color: '#64748b', maxWidth: '320px', textAlign: 'center' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #E2E8F0', borderTopColor: '#10B981', borderRadius: '50%', animation: 'numbrisSpin 0.9s linear infinite' }} />
          <div style={{ fontSize: '14px' }}>Loading…</div>
          <button
            type="button"
            onClick={() => { try { reset() } catch { window.location.reload() } }}
            style={{ marginTop: '4px', background: 'transparent', color: '#10B981', border: 'none', padding: '6px 10px', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}
          >
            Try again
          </button>
          <style dangerouslySetInnerHTML={{ __html: '@keyframes numbrisSpin { to { transform: rotate(360deg); } }' }} />
        </div>
      </body>
    </html>
  )
}
